import {
	Box,
	Button,
	CardActions,
	CardContent,
	FormHelperText,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { BoxSeriesType, BoxType } from '@/api/enum/common.enum';
import { API_DATE_TIME_FORMAT, isTime } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import AppDateTimePicker from '@/common/components/form-inputs/AppDateTimePicker';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { BOX_SERIES_TYPE, BOXTYPE } from '@/common/constants/common';
import AddSeriesDialog from './AddSeriesDialog';
import {
	useCreateBoxMutation,
	useUpdateBoxMutation,
} from '@/api/exchange/boxApiSlice';
import { BlindBoxDetailDTO } from '@/api/DTO/exchange/box.interface';

interface Props {
	type: FormType;
	details?: BlindBoxDetailDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function MysteryBoxInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createBox] = useCreateBoxMutation();
	const [updateBox] = useUpdateBoxMutation();

	const defaultValues: DefaultValues<BlindBoxDetailDTO> = details ?? {};
	useEffect(() => {
		if (type == FormType.edit) {
			// if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
			// if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
			// if (Number(details?.finishedCondition) > -1)
			// 	methods.setValue('finishedCondition', details?.finishedCondition);
			// if (details?.publishedStartAt)
			// 	methods.setValue('publishedStartAt', details.publishedStartAt);
			// if (details?.publishedEndAt)
			// 	methods.setValue('publishedEndAt', details.publishedEndAt);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		publishedStartAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		publishedEndAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		titleZh: Yup.string().required(t('errorMessage.required')),
		costPoints: Yup.string().required(t('errorMessage.required')),
		boxType: Yup.string().required(t('errorMessage.pleaseSelect')),
		gameType: Yup.string()
			.nullable()
			.when('boxType', {
				is: (boxType: string | number) => boxType == BoxType.SeriesBox,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
		gameSeriesId: Yup.string()
			.nullable()
			.when('boxType', {
				is: (boxType: string | number) => boxType == BoxType.SeriesBox,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
	});
	const methods = useForm<BlindBoxDetailDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchBoxType = methods.watch('boxType');
	const watchGameType = methods.watch('gameType');

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<BlindBoxDetailDTO> = async (data) => {
		const obj: BlindBoxDetailDTO = { ...data };
		const response =
			type == FormType.create
				? await createBox(obj)
				: await updateBox({ ...obj, id: details?.id || '' });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createBoxSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/exchange-manage/mystery-box');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/exchange-manage/mystery-box');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleGetSeries = (arr: any) => {
		if (arr.length > 0) {
			methods.setValue('gameSeriesId', arr[0].gameSeriesId);
			methods.setValue('gameSeriesName', arr[0].gameSeriesName);

			methods.trigger('gameSeriesId');
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('box.boxName')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('box.boxName_')}
											maxLength={100}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedStartAt'
									label={t('taskManage.pushedTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedEndAt'
									label={t('taskManage.pulledTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
								>
									<Typography
										variant='body2'
										sx={{
											display: 'flex',
										}}
									>
										<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
										&nbsp;
										{t('box.infoTips')}
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('box.boxType')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='boxType'
									row
									options={BOXTYPE}
								/>
							</Grid>
							{watchBoxType == BoxType.SeriesBox && (
								<>
									<Grid item xs={6}>
										<AppDropDownMenu
											control={methods.control}
											name='gameType'
											label={t('box.seriesType')}
											options={BOX_SERIES_TYPE.filter(
												(item) => item.id != BoxSeriesType.Chartlet
											)}
											handleChange={() => {
												methods.setValue('gameSeriesName', '');
												methods.setValue('gameSeriesId', '');
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<Box sx={{ display: 'flex' }}>
											<AppTextField
												disabled
												control={methods.control}
												name='gameSeriesName'
												label={t('taskManage.designatedSeries')}
											/>
											{watchGameType > -1 && (
												<AddSeriesDialog
													gameType={watchGameType}
													handleGetSeries={handleGetSeries}
												/>
											)}
										</Box>

										<FormHelperText style={{ color: '#d32f2f' }}>
											{typeof methods.formState.errors.gameSeriesId?.message ===
											'string'
												? methods.formState.errors.gameSeriesId.message
												: ''}
										</FormHelperText>
									</Grid>
								</>
							)}
							{(watchBoxType == BoxType.SeriesBox ||
								watchBoxType == BoxType.MixtureBox) && (
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='costPoints'
										label={t('box.needPoints')}
										inputType={InputType.number}
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
